import { useMutation } from '@tanstack/react-query';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { CadPrintResource, PrintingDataTypeEnum } from '@mark43/cad-api';
import { UnitHistoryForm } from '../types';
import { formatMonthDayYear } from '../../common/helpers/utils';
import { MAX_UNIT_HISTORY_RESULTS } from '../constants';
import { unitHistoryFormToElasticUserActionQuery } from '../unit-history-form-to-elastic-user-action-query';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useToast } from '@cad-arc';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { useExportsApi } from '../../exports/use-exports-api';

export function useExportUnitHistoryMutation() {
    const apiClient = useApiClient();
    const bootstrapState = useBootstrapState();
    const toast = useToast();
    const { refetchExports } = useExportsApi();
    return useMutation((unitHistoryForm: UnitHistoryForm) => {
        return apiClient
            .request<CadPrintResource.GetSearchPrintables>({
                method: 'GET',
                url: 'cad_print/search/USER_ACTION/printables',
            })
            .then((resp) => {
                const printables = resp.data.data;
                if (printables) {
                    return printables?.find((printable) =>
                        printable.title.includes('Unit History')
                    );
                }
            })
            .then((printable) => {
                if (printable) {
                    return apiClient
                        .request<CadPrintResource.StartMillingJobForUserActionSearch>({
                            method: 'POST',
                            url: 'cad_print/user_action',
                            // WorkOrders have minified properties, see BE for full names
                            data: [
                                {
                                    // dataType
                                    dt: PrintingDataTypeEnum.USER_ACTION_SEARCH.name,
                                    // from
                                    f: 0,
                                    isActingUserSearch: false,
                                    // paperFormat
                                    pf: 'PDF',
                                    // printingTemplateId
                                    ptId: printable.printingTemplateId,
                                    // size
                                    s: MAX_UNIT_HISTORY_RESULTS,
                                    search: unitHistoryFormToElasticUserActionQuery(
                                        unitHistoryForm
                                    ),
                                    // title
                                    t: formatUnitHistoryExportTitle(unitHistoryForm),

                                    // properties to make ts happy
                                    ar: false,
                                    c: [],
                                    cecIds: [],
                                    dId: bootstrapState.cadDepartmentAgencyView.departmentId,
                                    // exportId
                                    // @ts-expect-error don't want to set this
                                    eId: undefined,
                                },
                            ],
                        })
                        .then(() => {
                            refetchExports();
                        });
                } else {
                    toast({
                        status: 'default',
                        description: 'Could not export Unit History',
                    });
                }
            })
            .catch(
                createApiErrorHandler(() =>
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('exporting Unit History'),
                    })
                )
            );
    });
}

function formatUnitHistoryExportTitle(form: UnitHistoryForm): string {
    const unit = form.unitOption ? `Unit ${form.unitOption.label}` : undefined;
    const user = form.userOption?.label;
    const searchSubject = unit || user;
    const startDate = form.dateRange ? formatMonthDayYear(form.dateRange.start) : undefined;
    const endDate = form.dateRange ? formatMonthDayYear(form.dateRange.end) : undefined;
    if (searchSubject && startDate && endDate) {
        return `${searchSubject} History ${startDate} - ${endDate}`;
    } else {
        const today = formatMonthDayYear(new Date().toISOString());
        return `Unit History ${today}`;
    }
}
